import {useQueryClient} from "@tanstack/react-query";
import {useCallback} from "react";
import {useCustomQuery} from "../../../query/queryClient";
import {useStores} from "../../../stores";
import {supersetDashboardsQueryKey} from "../../chat-agent/hooks/useSupersetDashboards";
import {
    type DashboardItem,
    type DashboardList,
    fetchHermesDashboard,
    fetchHermesDashboards,
    fetchMyDashboards,
} from "./dashboardsApi";

const STALE_MS = 60_000;

// Keys are url-shaped to satisfy UrlQueryKey; they differ from the chat module's
// DashGen key because the cached shape differs (DashboardList vs raw records).
export const hermesDashboardsQueryKey = () => ["/dashboards/hermes"] as const;
export const hermesDashboardQueryKey = (id: string) => [`/dashboards/hermes/${id}`] as const;
export const myDashboardsQueryKey = (databagId: number) => [`/dashboards/mine?databag_id=${databagId}`] as const;

export function useHermesDashboards(enabled = true) {
    return useCustomQuery<DashboardList, Error, DashboardList, ReturnType<typeof hermesDashboardsQueryKey>>({
        queryKey: hermesDashboardsQueryKey(),
        queryFn: ({signal}) => fetchHermesDashboards(signal),
        enabled,
        staleTime: STALE_MS,
    });
}

export function useMyDashboards(databagId: number, enabled = true) {
    return useCustomQuery<DashboardList, Error, DashboardList, ReturnType<typeof myDashboardsQueryKey>>({
        queryKey: myDashboardsQueryKey(databagId),
        queryFn: ({signal}) => fetchMyDashboards(databagId, signal),
        enabled: enabled && !!databagId,
        staleTime: STALE_MS,
    });
}

export function useHermesDashboard(id: string, enabled = true) {
    return useCustomQuery<DashboardItem | null, Error, DashboardItem | null, ReturnType<typeof hermesDashboardQueryKey>>({
        queryKey: hermesDashboardQueryKey(id),
        queryFn: ({signal}) => fetchHermesDashboard(id, signal),
        enabled: enabled && !!id,
        staleTime: STALE_MS,
    });
}

export type DashboardsAvailability = {
    /** Both list probes have settled. */
    isReady: boolean;
    /** At least one list endpoint answered (a 404 on both means Superset is disabled). */
    isAvailable: boolean;
};

/**
 * Hides the Dashboards menu item / card when the agent's `/superset` routes 404.
 * Pass `enabled: false` when the card is hidden anyway, so nothing is fetched.
 */
export function useDashboardsAvailability(enabled = true): DashboardsAvailability {
    const {bagStore} = useStores();
    const hermes = useHermesDashboards(enabled);
    const mine = useMyDashboards(bagStore.bagId, enabled);
    if (!enabled) return {isReady: false, isAvailable: false};
    const mineSettled = !bagStore.bagId || !mine.isPending;
    const isReady = !hermes.isPending && mineSettled;
    // An error other than 404 (e.g. network) keeps the module visible: the page shows the error.
    const isAvailable = Boolean(
        hermes.data?.available || mine.data?.available || hermes.isError || mine.isError,
    );
    return {isReady, isAvailable};
}

/** Refresh both the Dashboards module's and the chat module's DashGen lists. */
export function useInvalidateMyDashboards() {
    const queryClient = useQueryClient();
    return useCallback((databagId: number) => Promise.all([
        queryClient.invalidateQueries({queryKey: myDashboardsQueryKey(databagId)}),
        queryClient.invalidateQueries({queryKey: supersetDashboardsQueryKey(databagId)}),
    ]), [queryClient]);
}
